import React, { useEffect, useState } from 'react';
import { animalApi, treatmentApi, costApi, feedingApi, inventoryApi, documentApi, masterDataApi } from '../services/api';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import { Animal, Treatment, Cost } from '../types';
import ConfirmModal from './ConfirmModal';

interface AnimalCardProps {
  animalId: number;
  onClose: () => void;
}

interface SaleData {
  sale_id: number;
  sale_date: string;
  buyer: string;
  weight_kg: number;
  price_per_kg: number;
  total_price: number;
  invoice_number?: string;
}

interface ProfitData {
  total_revenue: number;
  purchase_cost: number;
  operational_costs: number;
  total_costs: number;
  profit: number;
  profit_margin: number;
}

interface AnimalCardData {
  animal: Animal;
  treatments: Treatment[];
  costs: Cost[];
  calculated_data: {
    age_in_days: number;
    days_on_farm: number;
    estimated_current_weight: number;
    withdrawal_end_date?: string;
    can_be_slaughtered: boolean;
    cattle_passport_status?: {
      has_original: boolean;
      has_own: boolean;
      status_color: 'red' | 'orange' | 'green';
    };
  };
  sale_data?: SaleData;
  profit_data?: ProfitData;
  total_allocated_costs: number;
}

interface Feeding {
  id: number;
  feed: { id: number; name: string };
  feeding_type: 'individual' | 'box';
  quantity: number;
  quantity_unit: string;
  per_animal_quantity: number;
  fed_at: string;
  notes?: string;
  box?: { id: number; name: string };
}

const AnimalCard: React.FC<AnimalCardProps> = ({ animalId, onClose }) => {
  const [animalData, setAnimalData] = useState<AnimalCardData | null>(null);
  const [feedings, setFeedings] = useState<Feeding[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [showFeedingModal, setShowFeedingModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [availableFeeds, setAvailableFeeds] = useState<any[]>([]);
  const [feedQuantityUnits, setFeedQuantityUnits] = useState<any[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocumentType, setUploadDocumentType] = useState<string>('image');
  const [uploadDescription, setUploadDescription] = useState<string>('');
  const [deleteDocId, setDeleteDocId] = useState<number | null>(null);
  const [newFeeding, setNewFeeding] = useState({
    feed_inventory_id: '',
    quantity: '',
    quantity_unit: '',
    fed_at: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadAnimalData();
    loadFeedUnits();
  }, [animalId]);

  // Load feed inventory after animal data is available (need farm_id)
  useEffect(() => {
    if (animalData?.animal?.current_box?.stable?.farm_id) {
      loadFeedInventory(animalData.animal.current_box.stable.farm_id);
    }
  }, [animalData?.animal?.current_box?.stable?.farm_id]);

  const loadAnimalData = async () => {
    try {
      setLoading(true);
      const [cardResponse, treatmentsResponse, costsResponse, feedingsResponse, documentsResponse] = await Promise.all([
        animalApi.getAnimalCard(animalId),
        treatmentApi.getAnimalTreatments(animalId),
        costApi.getAnimalCosts(animalId),
        feedingApi.getAnimalFeedings(animalId),
        documentApi.getAnimalDocuments(animalId),
      ]);

      setAnimalData({
        animal: cardResponse.animal,
        treatments: treatmentsResponse,
        costs: Array.isArray(costsResponse) ? costsResponse : [],
        calculated_data: cardResponse.calculated_data,
        sale_data: cardResponse.sale_data,
        profit_data: cardResponse.profit_data,
        total_allocated_costs: cardResponse.total_allocated_costs || 0,
      });

      setFeedings(Array.isArray(feedingsResponse) ? feedingsResponse : []);
      setDocuments(documentsResponse.documents || []);
    } catch (error) {
      console.error('Error loading animal data:', error);
      handleApiError(error, 'Hiba történt az állat adatainak betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const formatAge = (days: number): string => {
    const years = Math.floor(days / 365);
    const remainingDaysAfterYears = days % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const remainingDays = remainingDaysAfterYears % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} év`);
    if (months > 0) parts.push(`${months} hónap`);
    if (remainingDays > 0 || parts.length === 0) parts.push(`${remainingDays} nap`);

    return parts.join(' ');
  };

  const loadFeedUnits = async () => {
    try {
      const category = await masterDataApi.getCategory('quantity_units');
      setFeedQuantityUnits(category.values || []);
    } catch (error) {
      console.error('Error loading feed units:', error);
      handleApiError(error, 'Hiba történt a mértékegységek betöltésekor');
    }
  };

  const loadFeedInventory = async (farmId: number) => {
    try {
      const feedsInStock = await inventoryApi.getFeeds(farmId, true);
      setAvailableFeeds(feedsInStock);
    } catch (error) {
      console.error('Error loading feed inventory:', error);
      handleApiError(error, 'Hiba történt a takarmánykészlet betöltésekor');
    }
  };

  const handleAddFeeding = async () => {
    try {
      if (!newFeeding.feed_inventory_id || !newFeeding.quantity) {
        showError('Kérlek töltsd ki a kötelező mezőket!');
        return;
      }

      // Check if enough feed is in stock
      const selectedFeed = availableFeeds.find((feed: any) =>
        feed.id.toString() === newFeeding.feed_inventory_id
      );

      if (selectedFeed && parseFloat(newFeeding.quantity) > selectedFeed.quantity) {
        showError(`Nincs elég takarmány készleten! Elérhető: ${selectedFeed.quantity} ${selectedFeed.quantity_unit}`);
        return;
      }

      const feedingData = {
        farm_id: animalData?.animal?.current_box?.stable?.farm_id,
        feed_inventory_id: parseInt(newFeeding.feed_inventory_id),
        animal_id: animalId,
        feeding_type: 'individual',
        quantity: parseFloat(newFeeding.quantity),
        fed_at: newFeeding.fed_at,
        notes: newFeeding.notes
      };

      await feedingApi.create(feedingData);
      showSuccess('Takarmányozás sikeresen rögzítve!');
      
      setShowFeedingModal(false);
      setNewFeeding({
        feed_inventory_id: '',
        quantity: '',
        quantity_unit: '',
        fed_at: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      loadAnimalData();
    } catch (error: any) {
      console.error('Error adding feeding:', error);
      handleApiError(error, 'Hiba történt a takarmányozás rögzítése során');
    }
  };

  const handleUploadDocument = async () => {
    try {
      if (!uploadFile) {
        showError('Kérlek válassz ki egy fájlt!');
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadDocumentType);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }

      await documentApi.uploadAnimalDocument(animalId, formData);
      showSuccess('Dokumentum sikeresen feltöltve!');

      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDocumentType('image');
      setUploadDescription('');

      loadAnimalData();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      handleApiError(error, 'Hiba történt a dokumentum feltöltése során');
    }
  };

  const handleDeleteDocument = async () => {
    if (deleteDocId === null) return;

    try {
      await documentApi.deleteDocument(deleteDocId);
      showSuccess('Dokumentum sikeresen törölve!');
      loadAnimalData();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      handleApiError(error, 'Hiba történt a dokumentum törlése során');
    } finally {
      setDeleteDocId(null);
    }
  };

  const handleDownloadDocument = async (documentId: number, filename: string) => {
    try {
      const blob = await documentApi.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      handleApiError(error, 'Hiba történt a dokumentum letöltése során');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
    }).format(amount);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!animalData) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">Hiba történt</h3>
            <p className="mt-1 text-sm text-gray-500">Az állat adatai nem tölthetők be.</p>
            <button
              onClick={onClose}
              className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Bezárás
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { animal, treatments, costs, calculated_data, sale_data, profit_data } = animalData;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">
            Állat karton - {animal.ear_tag}
          </h3>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFeedingModal(true)}
              disabled={!animal.current_box?.stable?.farm_id}
              title={!animal.current_box?.stable?.farm_id ? 'Az állat nincs boxhoz rendelve, nem takarmányozható' : ''}
              className={`${animal.current_box?.stable?.farm_id ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'} text-white px-4 py-2 rounded-md flex items-center gap-2`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Takarmányozás
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Alapadatok */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Alapadatok</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Fülszám:</span>
                <span className="font-medium">{animal.ear_tag}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fajta:</span>
                <span className="font-medium">{animal.breed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Nem:</span>
                <span className="font-medium">{animal.gender === 'him' ? 'Hím' : 'Nőstény'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Születési dátum:</span>
                <span className="font-medium">{formatDate(animal.birth_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Telepre érkezés:</span>
                <span className="font-medium">{animal.arrival_date ? formatDate(animal.arrival_date) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Színe:</span>
                <span className="font-medium">{animal.color || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Anya fülszáma:</span>
                <span className="font-medium">{animal.mother_ear_tag || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Származási ország:</span>
                <span className="font-medium">{animal.origin_country || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Származási tenyészet kódja:</span>
                <span className="font-medium">{animal.origin_farm_code || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Beszerzési számla:</span>
                <span className="font-medium">{animal.invoice_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bekerülési súly:</span>
                <span className="font-medium">{animal.purchase_weight ? `${animal.purchase_weight} kg` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Napi hízási érték:</span>
                <span className="font-medium">{animal.daily_weight_gain ? `${animal.daily_weight_gain} kg/nap` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Értékesítési számla:</span>
                <span className="font-medium">{animal.sale_invoice_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Telepről kikerülés dátuma:</span>
                <span className="font-medium">{animal.departure_date ? formatDate(animal.departure_date) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Marhalevél fizikailag megérkezett:</span>
                <span className={`font-medium ${animal.cattle_document_received ? 'text-green-600' : 'text-red-600'}`}>
                  {animal.cattle_document_received ? '✓ Igen' : '✗ Nem'}
                </span>
              </div>
            </div>
          </div>

          {/* Számított adatok */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Számított adatok</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Életkor:</span>
                <span className="font-medium">{formatAge(calculated_data.age_in_days)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Telepen eltöltött idő:</span>
                <span className="font-medium">{formatAge(calculated_data.days_on_farm)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Becsült jelenlegi súly:</span>
                <span className="font-medium">
                  {calculated_data.estimated_current_weight
                    ? `${calculated_data.estimated_current_weight.toFixed(1)} kg`
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vágható:</span>
                <span className={`font-medium ${calculated_data.can_be_slaughtered ? 'text-green-600' : 'text-red-600'}`}>
                  {calculated_data.can_be_slaughtered ? 'Igen' : 'Nem'}
                </span>
              </div>
              {calculated_data.withdrawal_end_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Visszavonási idő vége:</span>
                  <span className="font-medium">{formatDate(calculated_data.withdrawal_end_date)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Gazdasági adatok */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Gazdasági adatok</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Beszerzési súly:</span>
                <span className="font-medium">{animal.purchase_weight ? `${animal.purchase_weight} kg` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Beszerzési ár:</span>
                <span className="font-medium">{animal.purchase_price ? formatCurrency(animal.purchase_price) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Szállítási költség:</span>
                <span className="font-medium">{animal.transport_cost ? formatCurrency(animal.transport_cost) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Napi súlygyarapodás:</span>
                <span className="font-medium">{animal.daily_weight_gain} kg/nap</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Számla szám:</span>
                <span className="font-medium">{animal.invoice_number || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Szerződés szám:</span>
                <span className="font-medium">{animal.contract_number || '-'}</span>
              </div>
            </div>
          </div>

          {/* Státusz */}
          <div className="bg-yellow-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Státusz</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Jelenlegi státusz:</span>
                <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                  animal.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                  animal.status === 'eladott' ? 'bg-blue-100 text-blue-800' :
                  animal.status === 'levagott' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {animal.status.charAt(0).toUpperCase() + animal.status.slice(1)}
                </span>
              </div>
              {animal.status_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Státusz dátuma:</span>
                  <span className="font-medium">{formatDate(animal.status_date)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Jelenlegi box:</span>
                <span className="font-medium">{(animal as any).current_box?.name || 'Nincs megadva'}</span>
              </div>
            </div>
          </div>

          {/* Értékesítési adatok - Only shown for sold animals */}
          {sale_data && profit_data && (
            <>
              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Értékesítési adatok</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Értékesítés dátuma:</span>
                    <span className="font-medium">{formatDate(sale_data.sale_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vevő:</span>
                    <span className="font-medium">{sale_data.buyer}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Értékesítési súly:</span>
                    <span className="font-medium">{sale_data.weight_kg} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kilogrammonkénti ár:</span>
                    <span className="font-medium">{formatCurrency(sale_data.price_per_kg)}/kg</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600 font-semibold">Értékesítési ár:</span>
                    <span className="font-bold text-green-600">{formatCurrency(sale_data.total_price)}</span>
                  </div>
                  {sale_data.invoice_number && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Számla szám:</span>
                      <span className="font-medium">{sale_data.invoice_number}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Jövedelmezőségi elemzés</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bevétel:</span>
                    <span className="font-medium text-green-600">{formatCurrency(profit_data.total_revenue)}</span>
                  </div>
                  <div className="border-t pt-2 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Beszerzési költség:</span>
                      <span className="font-medium text-red-600">{formatCurrency(profit_data.purchase_cost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Működési költségek:</span>
                      <span className="font-medium text-red-600">{formatCurrency(profit_data.operational_costs)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600 font-semibold">Összes költség:</span>
                    <span className="font-bold text-red-600">{formatCurrency(profit_data.total_costs)}</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-purple-300 pt-3">
                    <span className="text-gray-900 font-bold">Realizált profit:</span>
                    <span className={`font-bold text-lg ${profit_data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(profit_data.profit)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profitmargin:</span>
                    <span className={`font-medium ${profit_data.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profit_data.profit_margin.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Kezelések */}
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Kezelések ({treatments.length})</h4>
          {treatments.length > 0 ? (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gyógyszer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dózis</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visszavonási idő</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Megjegyzés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kezelő</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {treatments.slice(0, 5).map((treatment) => (
                    <tr key={treatment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(treatment.treatment_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {treatment.medicine?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {treatment.dosage} {treatment.dosage_unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {treatment.withdrawal_days} nap
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {treatment.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {treatment.user?.name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Még nincsenek kezelések rögzítve.</p>
            </div>
          )}
        </div>

        {/* Költségek */}
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Kapcsolódó költségek ({costs.length}) - Össz: {costs.length > 0 ? formatCurrency(costs.reduce((sum, cost) => sum + cost.amount, 0)) : formatCurrency(0)}
          </h4>
          {costs.length > 0 ? (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategória</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leírás</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Összeg</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {costs.slice(0, 5).map((cost) => (
                    <tr key={cost.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(cost.cost_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cost.category}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {cost.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(cost.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Még nincsenek költségek hozzárendelve.</p>
            </div>
          )}
        </div>

        {/* Takarmányozási előzmények */}
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Takarmányozási előzmények ({feedings.length})
          </h4>

          {feedings.length > 0 ? (
            <div className="space-y-4">
              {/* Összesítő nézet */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                <h5 className="text-md font-semibold text-gray-900 mb-4">Összesített fogyasztás takarmánytípusonként</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(
                    feedings.reduce((acc, feeding) => {
                      const feedName = feeding.feed?.name || 'Ismeretlen';
                      if (!acc[feedName]) {
                        acc[feedName] = { kg: 0, count: 0 };
                      }
                      acc[feedName].kg += Number(feeding.per_animal_quantity) || 0;
                      acc[feedName].count += 1;
                      return acc;
                    }, {} as Record<string, { kg: number; count: number }>)
                  ).map(([feedName, data]) => (
                    <div key={feedName} className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{feedName}</span>
                        <span className="text-xs text-gray-500">({data.count} alkalom)</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {Number(data.kg).toFixed(2)} kg
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Átlag: {(Number(data.kg) / data.count).toFixed(2)} kg/alkalom
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-green-300">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Összes fogyasztás:</span>
                    <span className="text-2xl font-bold text-green-700">
                      {feedings.reduce((sum, f) => sum + (Number(f.per_animal_quantity) || 0), 0).toFixed(2)} kg
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
                    <span>Napi átlag ({calculated_data.days_on_farm} nap alapján):</span>
                    <span className="font-medium">
                      {calculated_data.days_on_farm > 0
                        ? (feedings.reduce((sum, f) => sum + (Number(f.per_animal_quantity) || 0), 0) / calculated_data.days_on_farm).toFixed(2)
                        : '0.00'
                      } kg/nap
                    </span>
                  </div>
                </div>
              </div>

              {/* Részletes napi lista */}
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="bg-gray-100 px-6 py-3 border-b">
                  <h5 className="text-md font-semibold text-gray-900">Részletes takarmányozási napló</h5>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Takarmány</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mennyiség/állat</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Megjegyzés</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedings.slice(0, 10).map((feeding) => (
                      <tr key={feeding.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(feeding.fed_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {feeding.feed?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {feeding.per_animal_quantity} {feeding.quantity_unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {feeding.feeding_type === 'individual' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Egyedi
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Box ({feeding.box?.name || '-'})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {feeding.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {feedings.length > 10 && (
                  <div className="bg-gray-50 px-6 py-3 text-center text-sm text-gray-500">
                    ...és további {feedings.length - 10} takarmányozási esemény
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Még nincsenek takarmányozási előzmények rögzítve.</p>
            </div>
          )}
        </div>

        {/* Szállítmány dokumentumok */}
        <div className="mt-6">
          <div className="border-2 rounded-lg p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h4 className="text-lg font-semibold text-gray-900">Szállítmány dokumentumok</h4>
            </div>

            {animal.deliveries && animal.deliveries.length > 0 ? (
              <div className="space-y-4">
                {animal.deliveries.map((delivery) => (
                  <div key={delivery.id} className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-medium text-gray-900">
                          Szállítmány - {formatDate(delivery.delivery_date)}
                        </h5>
                        <p className="text-sm text-gray-600">Eladó: {delivery.seller}</p>
                      </div>
                    </div>

                    {delivery.documents && delivery.documents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {delivery.documents.map((doc) => {
                          const documentTypeNames: Record<string, string> = {
                            'health_certificate': 'Állategészségügyi Bizonyítvány',
                            'livestock_declaration': 'Állattartói Nyilatkozat',
                            'cmr_consignment_note': 'CMR Fuvarlevél'
                          };

                          return (
                            <div key={doc.id} className="bg-gray-50 rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {documentTypeNames[doc.document_type] || doc.document_type}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mb-2 truncate" title={doc.original_filename}>
                                {doc.original_filename}
                              </div>
                              <div className="text-xs text-gray-500 mb-2">
                                {formatDate(doc.created_at)} - {formatFileSize(doc.file_size)}
                              </div>
                              <button
                                onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                                className="w-full bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs hover:bg-blue-100 flex items-center justify-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Letöltés
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Ehhez a szállítmányhoz nincsenek dokumentumok</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Nincs kapcsolódó szállítmány</p>
            )}
          </div>
        </div>

        {/* Marhalevél dokumentumok */}
        <div className="mt-6">
          <div className="border-2 rounded-lg p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4 className="text-lg font-semibold text-gray-900">Marhalevél dokumentumok</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Státusz:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  calculated_data.cattle_passport_status?.status_color === 'green'
                    ? 'bg-green-100 text-green-800'
                    : calculated_data.cattle_passport_status?.status_color === 'orange'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {calculated_data.cattle_passport_status?.status_color === 'green'
                    ? 'Teljes'
                    : calculated_data.cattle_passport_status?.status_color === 'orange'
                    ? 'Részleges'
                    : 'Hiányos'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Eredeti marhalevél */}
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                    Eredeti marhalevél
                    {calculated_data.cattle_passport_status?.has_original ? (
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </h5>
                </div>

                {documents.filter(doc => doc.document_type === 'cattle_passport_original').length > 0 ? (
                  <div className="space-y-2">
                    {documents.filter(doc => doc.document_type === 'cattle_passport_original').map((doc) => (
                      <div key={doc.id} className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{doc.original_filename}</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(doc.created_at)} - {formatFileSize(doc.file_size)}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                            className="flex-1 bg-green-50 text-green-600 px-2 py-1 rounded text-xs hover:bg-green-100 flex items-center justify-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Letöltés
                          </button>
                          <button
                            onClick={() => setDeleteDocId(doc.id)}
                            className="bg-red-50 text-red-600 px-2 py-1 rounded text-xs hover:bg-red-100"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Nincs feltöltve</p>
                )}

                <button
                  onClick={() => {
                    setUploadDocumentType('cattle_passport_original');
                    setShowUploadModal(true);
                  }}
                  className="w-full mt-3 bg-amber-600 text-white px-3 py-2 rounded-md hover:bg-amber-700 flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Eredeti marhalevél feltöltése
                </button>
              </div>

              {/* Saját marhalevél */}
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                    Saját marhalevél
                    {calculated_data.cattle_passport_status?.has_own ? (
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </h5>
                </div>

                {documents.filter(doc => doc.document_type === 'cattle_passport_own').length > 0 ? (
                  <div className="space-y-2">
                    {documents.filter(doc => doc.document_type === 'cattle_passport_own').map((doc) => (
                      <div key={doc.id} className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 truncate">{doc.original_filename}</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(doc.created_at)} - {formatFileSize(doc.file_size)}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                            className="flex-1 bg-green-50 text-green-600 px-2 py-1 rounded text-xs hover:bg-green-100 flex items-center justify-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Letöltés
                          </button>
                          <button
                            onClick={() => setDeleteDocId(doc.id)}
                            className="bg-red-50 text-red-600 px-2 py-1 rounded text-xs hover:bg-red-100"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Nincs feltöltve</p>
                )}

                <button
                  onClick={() => {
                    setUploadDocumentType('cattle_passport_own');
                    setShowUploadModal(true);
                  }}
                  className="w-full mt-3 bg-amber-600 text-white px-3 py-2 rounded-md hover:bg-amber-700 flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Saját marhalevél feltöltése
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Dokumentumok */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Dokumentumok és képek ({documents.length})
            </h4>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Fájl feltöltése
            </button>
          </div>
          {documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 truncate">{doc.original_filename}</h5>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(doc.created_at)}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      doc.document_type === 'image' ? 'bg-blue-100 text-blue-800' :
                      doc.document_type === 'pdf' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {doc.document_type}
                    </span>
                  </div>
                  {doc.description && (
                    <p className="text-sm text-gray-600 mb-3">{doc.description}</p>
                  )}
                  <div className="text-xs text-gray-500 mb-3">
                    {formatFileSize(doc.file_size)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                      className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded text-sm hover:bg-green-100 flex items-center justify-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Letöltés
                    </button>
                    <button
                      onClick={() => setDeleteDocId(doc.id)}
                      className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">Még nincsenek dokumentumok feltöltve.</p>
            </div>
          )}
        </div>

        {/* Megjegyzések */}
        {animal.notes && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Megjegyzések</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700">{animal.notes}</p>
            </div>
          </div>
        )}
      </div>

      {/* Takarmányozás Modal */}
      {showFeedingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Takarmányozás - {animal.ear_tag}</h3>
              <button
                onClick={() => setShowFeedingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAddFeeding(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Takarmány *
                  </label>
                  <select
                    value={newFeeding.feed_inventory_id}
                    onChange={(e) => {
                      const selected = availableFeeds.find((f: any) => f.id.toString() === e.target.value);
                      setNewFeeding({
                        ...newFeeding,
                        feed_inventory_id: e.target.value,
                        quantity_unit: selected ? selected.quantity_unit : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Válassz takarmányt...</option>
                    {availableFeeds.map((feed: any) => (
                      <option key={feed.id} value={feed.id}>
                        {feed.display_name || feed.feed?.name} - {feed.quantity} {feed.quantity_unit} készlet
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mennyiség *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newFeeding.quantity}
                    onChange={(e) => setNewFeeding({...newFeeding, quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Egység
                  </label>
                  <input
                    type="text"
                    value={newFeeding.quantity_unit}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    disabled
                    placeholder="Takarmány választásakor automatikus"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Takarmányozás dátuma *
                  </label>
                  <input
                    type="date"
                    value={newFeeding.fed_at}
                    onChange={(e) => setNewFeeding({...newFeeding, fed_at: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Megjegyzés
                  </label>
                  <textarea
                    value={newFeeding.notes}
                    onChange={(e) => setNewFeeding({...newFeeding, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowFeedingModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Takarmányozás rögzítése
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dokumentum feltöltés Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Dokumentum feltöltése - {animal.ear_tag}</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadDocumentType('image');
                  setUploadDescription('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleUploadDocument(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fájl *
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  accept="image/*,.pdf,.doc,.docx,.heic,.heif"
                />
                {uploadFile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Kiválasztva: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dokumentum típusa *
                </label>
                <select
                  value={uploadDocumentType}
                  onChange={(e) => setUploadDocumentType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="image">Kép</option>
                  <option value="pdf">PDF</option>
                  <option value="cattle_passport_original">Eredeti marhalevél</option>
                  <option value="cattle_passport_own">Saját marhalevél</option>
                  <option value="other">Egyéb</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leírás (opcionális)
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  maxLength={255}
                  placeholder="Pl. Oltási igazolás, egészségügyi vizsgálat..."
                />
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDocumentType('image');
                    setUploadDescription('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Feltöltés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteDocId !== null}
        onConfirm={handleDeleteDocument}
        onCancel={() => setDeleteDocId(null)}
        title="Dokumentum törlése"
        message="Biztosan törölni szeretnéd ezt a dokumentumot?"
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default AnimalCard;